import React, { useCallback, useRef, useState, useEffect } from 'react'
import './PlayerModal.scss'
import { onBackKeyPress, onEnterKeyPress } from '@utils/helpers'
import SpatialNavigation from '@utils/spatial-navigation.js'
import Title from '@components/Title/Title'
import { setAudio, setSubtitle } from '@actions/playerActions'
import { useDispatch } from 'react-redux'
import { Audio, Subtitle } from 'sdk'
import { useTypedSelector } from '../../store'
import ModalButton from './ModalButton/ModalButton'
import { disabledSubtitleValue, playerButtons, PlayerName } from '@constants/player.const'
import PrimaryPlayerButton from '@components/PlayerControls/PrimaryPlayerButton/PrimaryPlayerButton'
import { BackArrow } from '@assets/index'
import appConfig from '../../app_config.json'
import { VelvetSDK } from 'sdk'

interface PlayerModalProps {
  showModal?: boolean
  setShowModal: (status: boolean | ((prev: boolean) => boolean)) => void
  play: () => void
  pause: () => void
  continuePlaying: boolean
}

export const PlayerModal = ({ setShowModal, play, pause, continuePlaying }: PlayerModalProps) => {
  const SN = SpatialNavigation
  const [buttonActive, setButtonActive] = useState('')
  const subtitleRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLDivElement>(null)
  const offSubtitlesRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const savedSubtitle = useTypedSelector((state) => state.player?.subtitle)
  const savedAudio = useTypedSelector((state) => state.player?.audio)
  const subtitlesList = useTypedSelector((state) => state.player?.subtitleList)
  const audioList = useTypedSelector((state) => state.player?.audioList)
  const isPlaying = useTypedSelector((state) => state.player?.isPlaying)
  const V = VelvetSDK.Vocabulary.vocabulary
  const singleAudioItem = 1
  const noSubtitles = subtitlesList?.length === 0
  const hlsPlayer = appConfig.services.video_player.vendor_name === PlayerName.HLS
  const muxPlayer = appConfig.services.video_player.vendor_name === PlayerName.MUX

  const closePlayerModal = useCallback(() => {
    setShowModal((prev) => !prev)
    !continuePlaying && play()
    SN.focus(`#${playerButtons.AUDIO_AND_SUBTITLES_BUTTON}`)
  }, [SN, continuePlaying, play, setShowModal])

  const closePlayerModalOnBackKey = useCallback(
    (e: KeyboardEvent) => {
      onBackKeyPress(e, closePlayerModal)
    },
    [closePlayerModal],
  )

  const handleButton = useCallback((e: React.FocusEvent) => {
    setButtonActive(e.target.id)
  }, [])

  const handleUpdateStore = useCallback((e: React.KeyboardEvent, onKeyDown: () => void) => {
    e.preventDefault()
    onEnterKeyPress(e, () => onKeyDown())
  }, [])

  const handleAudioUpdate = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent, audio: Audio) => {
      const audioPayload = (hlsPlayer || muxPlayer)
        ? { id: audio.id.toString(), lang: e.currentTarget.id }
        : { id: e.currentTarget.id, lang: audio.lang }

      dispatch(setAudio(audioPayload))
    },
    [dispatch, hlsPlayer, muxPlayer],
  )

  const handleSubtitleUpdate = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent, subtitle: Subtitle) => {
      const subtitlePayload = { id: e.currentTarget.id, lang: subtitle.lang }

      dispatch(setSubtitle(subtitlePayload))
    },
    [dispatch],
  )

  useEffect(() => {
    if (!continuePlaying) {
      pause()
    } else {
      if (isPlaying) {
        play()
      } else {
        pause()
      }
    }
  }, [continuePlaying, isPlaying, pause, play])

  useEffect(() => {
    document.addEventListener('keydown', closePlayerModalOnBackKey, true)

    return () => document.removeEventListener('keydown', closePlayerModalOnBackKey, true)
  })

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.focus()
    } else if (subtitleRef.current) {
      subtitleRef.current.focus()
    } else if (offSubtitlesRef.current) {
      offSubtitlesRef.current.focus()
    }
  }, [])

  const focusAudioButton = (audio: Audio) => {
    if (savedAudio && audio.lang === savedAudio.lang) {
      return audioRef
    }
  }

  const buttonClassname = (itemType: string, itemId: string) => `${itemType} ${buttonActive === itemId ? 'active' : ''}`

  const labelClassname = (condition: boolean) => (condition ? 'chosen' : '')

  return (
    <div className="modal player-modal-focus">
      <div className="inner">
        <PrimaryPlayerButton onClick={closePlayerModal} className="back-button">
          <BackArrow />
        </PrimaryPlayerButton>
        {audioList?.length > 0 && (
          <div className="audio-part">
            <Title title={V.playerAudio} />
            {audioList.map((audio: Audio, index: number) => (
              <ModalButton
                key={audio.id}
                id={audio.lang}
                buttonClassName={buttonClassname('audio-item', audio.lang.toString())}
                onFocus={(e) => handleButton(e)}
                onKeyDown={(e) => handleUpdateStore(e, () => handleAudioUpdate(e, audio))}
                onClick={(e) => handleAudioUpdate(e, audio)}
                buttonRef={focusAudioButton(audio)}
                labelClassName={labelClassname(
                  savedAudio.lang === audio.lang.toString() ||
                    savedAudio.id === audio.id.toString() ||
                    audioList.length === singleAudioItem,
                )}
                label={audio.lang}
                onFocusDown={index === audioList.length - 1 ? '' : undefined}
              />
            ))}
          </div>
        )}
        <div className="subtitles-part">
          <Title title={V.playerSubtitles} />
          {subtitlesList?.length > 0 &&
            subtitlesList.map((subtitle: Subtitle) => (
              <ModalButton
                key={subtitle.id}
                id={subtitle.lang}
                buttonClassName={buttonClassname('subtitle-item', subtitle.lang?.toString())}
                onFocus={(e) => handleButton(e)}
                onKeyDown={(e) => handleUpdateStore(e, () => handleSubtitleUpdate(e, subtitle))}
                onClick={(e) => handleSubtitleUpdate(e, subtitle)}
                buttonRef={subtitle.lang?.toString() === savedSubtitle.lang ? subtitleRef : null}
                labelClassName={savedSubtitle && labelClassname(savedSubtitle.lang === subtitle.lang?.toString())}
                label={subtitle.lang}
              />
            ))}
          <ModalButton
            id={disabledSubtitleValue}
            buttonClassName={buttonClassname('subtitle-item', disabledSubtitleValue)}
            onFocus={(e) => handleButton(e)}
            onKeyDown={(e) =>
              handleUpdateStore(e, () =>
                dispatch(setSubtitle({ id: disabledSubtitleValue, lang: disabledSubtitleValue })),
              )
            }
            onClick={() => dispatch(setSubtitle({ id: disabledSubtitleValue, lang: disabledSubtitleValue }))}
            buttonRef={offSubtitlesRef}
            labelClassName={labelClassname(
              savedSubtitle.lang === disabledSubtitleValue || savedSubtitle.lang === '' || noSubtitles,
            )}
            label={V.playerOffButton}
          />
        </div>
      </div>
    </div>
  )
}
